import _regenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/regenerator.js";
import _objectSpread from "/usr/app/node_modules/@babel/runtime/helpers/esm/objectSpread2.js";
import _asyncToGenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _slicedToArray from "/usr/app/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import { mapSelected } from "@/pages/search/searchHooks/utils";
import { handleFilter, mapFacets } from "@/apis/search/searchUtils";
import { searchLabGet } from "@/apis/searchLab/searchLabClient";
import NotFound404Exception from "@/exceptions/NotFound404Exception";
import InternalServerError500Exception from "@/exceptions/InternalServerError500Exception";

/**
 * search-lab endpoint client (SE-12165).
 *
 * One function per route in routes/api.php. Each returns the same shapes the
 * legacy `/search/index` consumers already expect (SE-12166 aligned the
 * response contract), so callers can switch path with no response remapping.
 */

/**
 * search-lab only exposes `index`, `browse` and `similar`. The condition-based
 * catalog variants (used-cars / new-cars / all-cars) are NOT search-lab routes;
 * those queries go through the generic index endpoint with their filters intact.
 */

/**
 * search-lab validates `includes` as a CSV string (SearchRequest rules), while
 * the legacy frontend passes an array. Normalize to the CSV the API expects.
 */
var includesToCsv = function includesToCsv(includes) {
  return Array.isArray(includes) ? includes.join(",") : includes || "";
};

/**
 * Drop `undefined` / `null` entries before they hit the query string.
 * On a POST body a missing key simply isn't there; on a GET, axios would emit
 * `size=` and Laravel's `nullable|integer` rules reject the empty string.
 */
var pruneParams = function pruneParams(params) {
  return Object.fromEntries(Object.entries(params).filter(function (_ref) {
    var _ref2 = _slicedToArray(_ref, 2),
      value = _ref2[1];
    return value !== undefined && value !== null && value !== "";
  }));
};
var unwrap = function unwrap(res, ctx) {
  var _res$data, _res$data2, _res$data$data, _res$data4;
  if ((res === null || res === void 0 ? void 0 : (_res$data = res.data) === null || _res$data === void 0 ? void 0 : _res$data.success) === false || res !== null && res !== void 0 && (_res$data2 = res.data) !== null && _res$data2 !== void 0 && _res$data2.code && res.data.code >= 400) {
    var _res$data3;
    throw new InternalServerError500Exception("searchLab__" + ctx, {
      url: ctx,
      data: res === null || res === void 0 ? void 0 : (_res$data3 = res.data) === null || _res$data3 === void 0 ? void 0 : _res$data3.error
    });
  }
  return (_res$data$data = res === null || res === void 0 ? void 0 : (_res$data4 = res.data) === null || _res$data4 === void 0 ? void 0 : _res$data4.data) !== null && _res$data$data !== void 0 ? _res$data$data : {};
};

/**
 * Run a catalog search against the chosen endpoint.
 * `endpoint=""` hits GET /{lang}/index (general keyword search).
 */
export var searchLab__search = /*#__PURE__*/function () {
  var _ref3 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee(req, endpoint, body) {
    var _req$query;
    var params, res, data;
    return _regenerator().w(function (_context) {
      while (1) switch (_context.n) {
        case 0:
          if (!(req !== null && req !== void 0 && (_req$query = req.query) !== null && _req$query !== void 0 && _req$query.page && (+req.query.page < 1 || isNaN(+req.query.page)))) {
            _context.n = 1;
            break;
          }
          throw new NotFound404Exception("searchLab__wrong_page_validation");
        case 1:
          params = pruneParams(_objectSpread(_objectSpread({}, body), {}, {
            includes: includesToCsv(body.includes)
          }));
          _context.n = 2;
          return searchLabGet(req, "search", endpoint, params);
        case 2:
          res = _context.v;
          data = unwrap(res, "search/".concat(endpoint || "index")); // SE-13499: `?page=N` outside the series must 404 from SSR, including when the
          // catalog returns no pages at all (`total_pages` 0 / missing) — guarding on the
          // truthiness of total_pages let those render a thin 200 page instead.
          // Page 1 is exempt: an empty listing is a legitimate 200 empty state, and the
          // widget queries on autos pages always ask for page 1.
          if (!(+body.page > 1 && +body.page > (+data.total_pages || 0))) {
            _context.n = 3;
            break;
          }
          throw new NotFound404Exception("searchLab__page_exceeds_total_pages");
        case 3:
          return _context.a(2, data);
      }
    }, _callee);
  }));
  return function searchLab__search(_x, _x2, _x3) {
    return _ref3.apply(this, arguments);
  };
}();

/**
 * Pick the search-lab endpoint for a catalog query.
 * Always use /search/index — it resolves the SEO `link` (still sent in the
 * body) into make/model/country filters itself, and returns the same shape as
 * /search/browse.
 */
var resolveEndpoint = function resolveEndpoint(_link) {
  return "";
};

/**
 * Drop-in replacement for the legacy `api__doSearch_with_params`: same input
 * args, same `ISearchResultsRedux` output.
 */
export var searchLab__doSearch_with_params = /*#__PURE__*/function () {
  var _ref5 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee2(_ref4) {
    var req, _ref4$page, page, filters, _ref4$sort, sort, _ref4$link, link, _ref4$size, size, includes, endpoint, body, searchResults;
    return _regenerator().w(function (_context2) {
      while (1) switch (_context2.n) {
        case 0:
          req = _ref4.req, _ref4$page = _ref4.page, page = _ref4$page === void 0 ? 1 : _ref4$page, filters = _ref4.filters, _ref4$sort = _ref4.sort, sort = _ref4$sort === void 0 ? "" : _ref4$sort, _ref4$link = _ref4.link, link = _ref4$link === void 0 ? "" : _ref4$link, _ref4$size = _ref4.size, size = _ref4$size === void 0 ? 15 : _ref4$size, includes = _ref4.includes;
          filters = handleFilter(filters || {});
          endpoint = resolveEndpoint(link);
          body = {
            filters: filters,
            text: filters.text || "",
            link: link || "",
            page: page,
            size: size,
            sort: sort || "",
            includes: includes
          };
          _context2.n = 1;
          return searchLab__search(req, endpoint, body);
        case 1:
          searchResults = _context2.v;
          return _context2.a(2, {
            posts: searchResults.products,
            filters: mapFacets(searchResults.facets) || [],
            sortOptions: searchResults.sort_options,
            marketingBanner: {
              desktop: searchResults.marketing_banner_desktop,
              mobile: searchResults.marketing_banner_mobile
            },
            similarItems: searchResults.similar_items,
            dealFacet: searchResults.deal,
            specs: searchResults.specs,
            selectedOptions: mapSelected(searchResults.selected),
            metaData: {
              seoData: searchResults.seo_data,
              totalPages: searchResults.total_pages,
              title: searchResults.title,
              currentPage: Math.min(searchResults.total_pages, searchResults.page),
              productsCount: searchResults.products_count,
              category_path: searchResults.category_path,
              request_id: searchResults.request_id,
              metaTags: searchResults.meta_tags
            },
            search_meta: searchResults.search_meta
          });
      }
    }, _callee2);
  }));
  return function searchLab__doSearch_with_params(_x4) {
    return _ref5.apply(this, arguments);
  };
}();

/** Raw catalog search returning the unshaped `ISearchResults` (legacy `api__doSearch`). */
export var searchLab__doSearch = /*#__PURE__*/function () {
  var _ref6 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee3(req) {
    var _data$filters;
    var page,
      data,
      includes,
      filters,
      endpoint,
      _args3 = arguments;
    return _regenerator().w(function (_context3) {
      while (1) switch (_context3.n) {
        case 0:
          page = _args3.length > 1 && _args3[1] !== undefined ? _args3[1] : 1;
          data = _args3.length > 2 && _args3[2] !== undefined ? _args3[2] : {};
          includes = _args3.length > 3 && _args3[3] !== undefined ? _args3[3] : [];
          filters = handleFilter(_objectSpread({}, (_data$filters = data === null || data === void 0 ? void 0 : data.filters) !== null && _data$filters !== void 0 ? _data$filters : data));
          endpoint = resolveEndpoint(data === null || data === void 0 ? void 0 : data.link);
          return _context3.a(2, searchLab__search(req, endpoint, {
            filters: filters,
            text: filters.text || "",
            link: (data === null || data === void 0 ? void 0 : data.link) || "",
            page: page,
            size: data === null || data === void 0 ? void 0 : data.size,
            sort: (data === null || data === void 0 ? void 0 : data.sort) || "",
            includes: includes
          }));
      }
    }, _callee3);
  }));
  return function searchLab__doSearch(_x5) {
    return _ref6.apply(this, arguments);
  };
}();

// ── Similar items ────────────────────────────────────────────────────────────
export var searchLab__similar = /*#__PURE__*/function () {
  var _ref7 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee4(req, filters) {
    var _res$data5;
    var size,
      res,
      products,
      _args4 = arguments;
    return _regenerator().w(function (_context4) {
      while (1) switch (_context4.n) {
        case 0:
          size = _args4.length > 2 && _args4[2] !== undefined ? _args4[2] : 20;
          _context4.n = 1;
          return searchLabGet(req, "search", "similar", pruneParams({
            filters: filters,
            size: size
          }));
        case 1:
          res = _context4.v;
          // syarah similar-items envelope: { success, code, data: [...products], errors }.
          // Products live directly in `data` as a flat array.
          products = Array.isArray(res === null || res === void 0 ? void 0 : (_res$data5 = res.data) === null || _res$data5 === void 0 ? void 0 : _res$data5.data) ? res.data.data : [];
          return _context4.a(2, {
            numberOfProducts: products.length,
            products: products
          });
      }
    }, _callee4);
  }));
  return function searchLab__similar(_x6, _x7) {
    return _ref7.apply(this, arguments);
  };
}();

// ── By post ids (recently viewed) ────────────────────────────────────────────
/**
 * Fetch product cards for an explicit, ordered list of post ids.
 *
 * GET /{lang}/by-post-ids?post_ids[]=..&size=..
 * Envelope: { success, code, data: [...products], errors } — products live
 * directly in `data` as a flat array (same as syarah). The API preserves the
 * requested id order, so the returned array is already ordered.
 */
export var searchLab__byPostIds = /*#__PURE__*/function () {
  var _ref8 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee5(req, postIds, size) {
    var _res$data6;
    var res;
    return _regenerator().w(function (_context5) {
      while (1) switch (_context5.n) {
        case 0:
          _context5.n = 1;
          return searchLabGet(req, "search", "by-post-ids", pruneParams({
            post_ids: postIds,
            size: size
          }));
        case 1:
          res = _context5.v;
          return _context5.a(2, Array.isArray(res === null || res === void 0 ? void 0 : (_res$data6 = res.data) === null || _res$data6 === void 0 ? void 0 : _res$data6.data) ? res.data.data : []);
      }
    }, _callee5);
  }));
  return function searchLab__byPostIds(_x8, _x9, _x0) {
    return _ref8.apply(this, arguments);
  };
}();

// ── New-home product sections ────────────────────────────────────────────────
/**
 * New-home sections sourced from search-lab (replaces syarah SiteController
 * new-home includes: all_posts, hot_deals, deal_offers). Each returns the same
 * envelope shape the legacy /site/new-home include produced, so HomePage
 * consumes them unchanged.
 *
 * Envelope: { success, data: <section>, error, ... } — section lives in `data`.
 */

/** all_posts: { title, posts, search_filter }. React reads `.posts`. */
export var searchLab__homeAllPosts = /*#__PURE__*/function () {
  var _ref9 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee6(req) {
    var _res$data$data2, _res$data7;
    var count,
      res,
      _args6 = arguments;
    return _regenerator().w(function (_context6) {
      while (1) switch (_context6.n) {
        case 0:
          count = _args6.length > 1 && _args6[1] !== undefined ? _args6[1] : 8;
          _context6.n = 1;
          return searchLabGet(req, "search", "all-posts", {
            count: count
          });
        case 1:
          res = _context6.v;
          return _context6.a(2, (_res$data$data2 = res === null || res === void 0 ? void 0 : (_res$data7 = res.data) === null || _res$data7 === void 0 ? void 0 : _res$data7.data) !== null && _res$data$data2 !== void 0 ? _res$data$data2 : {
            title: "",
            posts: [],
            search_filter: null
          });
      }
    }, _callee6);
  }));
  return function searchLab__homeAllPosts(_x1) {
    return _ref9.apply(this, arguments);
  };
}();

/** hot_deals: array of { title, posts, position, deal_id } (positions 1 & 2). */
export var searchLab__homeHotDeals = /*#__PURE__*/function () {
  var _ref0 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee7(req) {
    var _res$data8;
    var count,
      res,
      _args7 = arguments;
    return _regenerator().w(function (_context7) {
      while (1) switch (_context7.n) {
        case 0:
          count = _args7.length > 1 && _args7[1] !== undefined ? _args7[1] : 4;
          _context7.n = 1;
          return searchLabGet(req, "search", "hot-deals", {
            count: count
          });
        case 1:
          res = _context7.v;
          return _context7.a(2, Array.isArray(res === null || res === void 0 ? void 0 : (_res$data8 = res.data) === null || _res$data8 === void 0 ? void 0 : _res$data8.data) ? res.data.data : []);
      }
    }, _callee7);
  }));
  return function searchLab__homeHotDeals(_x10) {
    return _ref0.apply(this, arguments);
  };
}();

/** deal_offers: { posts, title, search_filter, show_web, show_mobile }. */
export var searchLab__homeDealOffers = /*#__PURE__*/function () {
  var _ref1 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee8(req) {
    var _res$data$data3, _res$data9;
    var count,
      isNew,
      res,
      _args8 = arguments;
    return _regenerator().w(function (_context8) {
      while (1) switch (_context8.n) {
        case 0:
          count = _args8.length > 1 && _args8[1] !== undefined ? _args8[1] : 6;
          isNew = _args8.length > 2 && _args8[2] !== undefined ? _args8[2] : null;
          _context8.n = 1;
          return searchLabGet(req, "search", "deal-offers", {
            count: count,
            is_new: isNew
          });
        case 1:
          res = _context8.v;
          return _context8.a(2, (_res$data$data3 = res === null || res === void 0 ? void 0 : (_res$data9 = res.data) === null || _res$data9 === void 0 ? void 0 : _res$data9.data) !== null && _res$data$data3 !== void 0 ? _res$data$data3 : {
            posts: [],
            title: "",
            search_filter: null,
            show_web: true,
            show_mobile: true
          });
      }
    }, _callee8);
  }));
  return function searchLab__homeDealOffers(_x11) {
    return _ref1.apply(this, arguments);
  };
}();

// ── Autosuggest ───────────────────────────────────────────────────────────────
/** GET /{lang}/autosuggest?q=... — envelope `{ success, code, data: { numberOfProducts, products: [{autosuggest}] }, error }`. */
export var searchLab__autosuggest = /*#__PURE__*/function () {
  var _ref10 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee9(req, searchTerm) {
    var _res$data0, _res$data0$data;
    var res, products;
    return _regenerator().w(function (_context9) {
      while (1) switch (_context9.n) {
        case 0:
          _context9.n = 1;
          return searchLabGet(req, "search", "autosuggest", {
            q: searchTerm
          });
        case 1:
          res = _context9.v;
          products = res === null || res === void 0 ? void 0 : (_res$data0 = res.data) === null || _res$data0 === void 0 ? void 0 : (_res$data0$data = _res$data0.data) === null || _res$data0$data === void 0 ? void 0 : _res$data0$data.products;
          return _context9.a(2, Array.isArray(products) ? products : []);
      }
    }, _callee9);
  }));
  return function searchLab__autosuggest(_x12, _x13) {
    return _ref10.apply(this, arguments);
  };
}();

// ── Recommendations (recommended only) ───────────────────────────────────────
export var searchLab__recommendations = /*#__PURE__*/function () {
  var _ref11 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee0(req) {
    var _ref12, _res$data$data4, _res$data1, _res$data10;
    var params,
      res,
      _args0 = arguments;
    return _regenerator().w(function (_context0) {
      while (1) switch (_context0.n) {
        case 0:
          params = _args0.length > 1 && _args0[1] !== undefined ? _args0[1] : {};
          _context0.n = 1;
          return searchLabGet(req, "search", "recommended", params);
        case 1:
          res = _context0.v;
          return _context0.a(2, (_ref12 = (_res$data$data4 = res === null || res === void 0 ? void 0 : (_res$data1 = res.data) === null || _res$data1 === void 0 ? void 0 : _res$data1.data) !== null && _res$data$data4 !== void 0 ? _res$data$data4 : res === null || res === void 0 ? void 0 : (_res$data10 = res.data) === null || _res$data10 === void 0 ? void 0 : _res$data10.products) !== null && _ref12 !== void 0 ? _ref12 : []);
      }
    }, _callee0);
  }));
  return function searchLab__recommendations(_x14) {
    return _ref11.apply(this, arguments);
  };
}();